import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import {
  FlatJoinSplitV2MerkleTree,
  readFlatJoinSplitV2TreeHistory,
  type FlatJoinSplitV2TreeScanRequest,
} from "./merkleIndex";
import { readPinnedPoolCall } from "./pinnedPoolReads";

const ABI = new ethers.Interface([
  "function currentTreeIndex() view returns(uint256)",
  "function merkleRoot() view returns(bytes32)",
  "function nextLeafIndex() view returns(uint256)",
]);
type SavedTree = {
  version: 1;
  block: number;
  hash: string;
  root: string;
  leaves: string[];
};

/** Public leaves only. A persisted prefix is rebuilt and checked against the
 * actual pool root/count at its historical block before it can save any reads.
 * Metadata corruption, missing storage and reorgs fall back to a full scan. */
export async function readBalanceTreeHistory(
  request: Omit<FlatJoinSplitV2TreeScanRequest, "existingLeaves">,
) {
  return readPublicTreeHistory(request, false);
}

/** Spending can extend beyond the recovered safe block. Seed its independent
 * cache from the verified recovery checkpoint without moving that checkpoint
 * forward or replacing journal history, which also needs event metadata. */
export async function readSpendTreeHistory(
  request: Omit<FlatJoinSplitV2TreeScanRequest, "existingLeaves">,
) {
  return readPublicTreeHistory(request, true);
}

async function readPublicTreeHistory(
  request: Omit<FlatJoinSplitV2TreeScanRequest, "existingLeaves">,
  forSpend: boolean,
) {
  // Keep the existing path on networks outside the incremental sync rollout.
  if (
    !EVM_CONFIG.JOINSPLIT_V2.INCREMENTAL_SYNC ||
    typeof window === "undefined"
  )
    return readFlatJoinSplitV2TreeHistory(request);
  const key = `stealthos_public_tree_v1:${EVM_CONFIG.CHAIN_ID}:${ethers.getAddress(request.pool).toLowerCase()}:${request.treeIndex}:${request.fromBlock}`;
  return readFlatJoinSplitV2TreeHistory(request, {
    key: forSpend ? `${key}:spend` : key,
    load: async () => {
      try {
        const raw = window.localStorage?.getItem(key);
        if (!raw || raw.length > 7_000_000) return;
        const saved = JSON.parse(raw) as SavedTree;
        if (
          saved.version !== 1 ||
          !Number.isSafeInteger(saved.block) ||
          saved.block < request.fromBlock ||
          saved.block > request.toBlock ||
          !ethers.isHexString(saved.hash, 32) ||
          !ethers.isHexString(saved.root, 32) ||
          !Array.isArray(saved.leaves) ||
          saved.leaves.length > 100_000
        )
          return;
        const provider = request.provider as ethers.Provider;
        const block = await provider.getBlock(saved.block);
        if (
          block?.hash?.toLowerCase() !== saved.hash ||
          block.number !== saved.block
        )
          return;
        const read = async (name: string) =>
          ABI.decodeFunctionResult(
            name,
            await readPinnedPoolCall(
              provider,
              request.pool,
              ABI.encodeFunctionData(name),
              saved.block,
              EVM_CONFIG.CHAIN_ID,
            ),
          )[0];
        const [index, root, count, tree] = await Promise.all([
          read("currentTreeIndex"),
          read("merkleRoot"),
          read("nextLeafIndex"),
          FlatJoinSplitV2MerkleTree.create(request.treeIndex, saved.leaves),
        ]);
        if (
          BigInt(index) !== BigInt(request.treeIndex) ||
          String(root).toLowerCase() !== tree.root ||
          tree.root !== saved.root ||
          BigInt(count) !== BigInt(tree.nextLeafIndex)
        )
          return;
        return {
          block: saved.block,
          hash: saved.hash,
          value: {
            pool: ethers.getAddress(request.pool),
            treeIndex: request.treeIndex,
            fromBlock: request.fromBlock,
            toBlock: saved.block,
            toBlockHash: saved.hash,
            leaves: tree.getLeaves(),
            insertions: [],
            root: tree.root,
            nextLeafIndex: tree.nextLeafIndex,
            tree,
          },
        };
      } catch {
        return;
      }
    },
    save: async (checkpoint) => {
      if (forSpend) return;
      // Only save a root/count explicitly read from the currently active tree.
      // After rollover, keep the last verified active prefix for this old tree.
      if (!request.expectedRoot || request.expectedNextLeafIndex === undefined)
        return;
      try {
        window.localStorage?.setItem(
          key,
          JSON.stringify({
            version: 1,
            block: checkpoint.block,
            hash: checkpoint.hash,
            root: checkpoint.value.root,
            leaves: checkpoint.value.leaves,
          } satisfies SavedTree),
        );
      } catch {
        /* Quota/private browsing affects speed only, never recovery. */
      }
    },
  });
}
